import httpClient from './httpClient';
import { AxiosResponse } from 'axios';
import { getCurrentlyActiveOrganization, OrganizationAccessRoles } from '../common/auth';
import { User } from '../types/User';
import { SignUpMethod } from '../common/signUpMethod';
import { createCode, resendCode } from 'supertokens-web-js/recipe/passwordless';

const USER_BASE_ROUTE = '/v1/user';

export interface VerifyEmailLoginResponse {
  accessToken: string;
  refreshToken: string;
}

export interface LoginUsingPasswordResponse {
  accessToken: string;
  refreshToken: string;
}

export interface VerifyEmailRegistrationResponse {
  accessToken: string;
  refreshToken: string;
}

export interface SignupUsingPasswordResponse {
  email: string;
  role: string;
  status: string;
  accessToken: string;
  refreshToken: string;
}

export interface LoginResponseModel {
  accessToken: string;
  refreshToken: string;
}

export interface UserWithInviteResponse {
  userId: number;
  fullName?: string;
  email: string;
  status: string;
  role: OrganizationAccessRoles;
  createdAt: Date;
  updatedAt: Date;
  invitedBy: User;
}

export interface InviteUsersToOrganizationResponse {
  invitedUsers: UserWithInviteResponse[];
}

/**
 * Yes there is more... add as needed.
 */
export interface UserProfilePictureUploadResponse {
  data: {
    path: string;
  };
  accessToken: string; // This is basically AccessTokenUser model encoded in JWT
}

export async function triggerEmailRegistrationFlow(email: string) {
  const response = await createCode({
    email,
  });

  return response;
}

export async function triggerResendEmailRegistrationFlow() {
  const response = await resendCode();
  return response;
}

export async function triggerEmailLoginFlow(email: string) {
  const { data } = await checkIfUserExists(email);
  // A Google account cannot log in with a magic link, so nothing is sent and Login offers Google.
  if (data.signUpMethod === 'google') {
    return { status: 'ACCOUNT_USES_GOOGLE' } as const;
  }

  const response = await createCode({
    email,
  });

  return response;
}

export type VerifyEmailInvitationResponse = {
  accessToken: string;
  refreshToken: string;
};

export async function verifyEmailInvitation(
  userId: number,
  token: string
): Promise<AxiosResponse<VerifyEmailInvitationResponse>> {
  return await httpClient({
    method: 'POST',
    url: `${USER_BASE_ROUTE}/verify-magic-link-invite`,
    data: {
      token,
      userId,
    },
  });
}

export async function verifyEmailRegistration(
  userId: number,
  token: string
): Promise<AxiosResponse<VerifyEmailRegistrationResponse>> {
  return await httpClient({
    method: 'POST',
    url: `${USER_BASE_ROUTE}/verify-magic-link-registration`,
    data: {
      token,
      userId,
    },
  });
}

export async function verifyEmailLogin(
  userId: number,
  token: string
): Promise<AxiosResponse<VerifyEmailLoginResponse>> {
  return await httpClient({
    method: 'POST',
    url: `${USER_BASE_ROUTE}/verify-magic-link-login`,
    data: {
      token,
      userId,
    },
  });
}

// One or both have to be sent, sending none results in an error.
export type updateUserRequest = {
  email?: string;
  fullName?: string | null;
  profilePicture?: string | null;
  profilePicturePath?: string | null;
};

export type UpdateUserResponse = {
  role: string;
  status: string;
  email: string;
  fullName: string;
  profilePicture: string | null;
  profilePicturePath: string | null;
};

export async function updateUser(data: updateUserRequest): Promise<AxiosResponse<UpdateUserResponse>> {
  return await httpClient({
    method: 'PATCH',
    url: `${USER_BASE_ROUTE}/`,
    data,
  });
}

export type SelfSortResponse = {
  jobRole: string;
  selfSortCompletedAt: string;
};

/**
 * Dedicated self-sort completion write. Sends only the role; the server
 * validates it, stamps completion, and refreshes the JWT.
 */
export async function submitSelfSort(jobRole: string): Promise<AxiosResponse<SelfSortResponse>> {
  return await httpClient({
    method: 'POST',
    url: `${USER_BASE_ROUTE}/self-sort`,
    data: { jobRole },
  });
}

export type SetUserPasswordRequest = {
  password: string;
  passwordConfirmation: string;
};

/**
 * Sets the user password.
 */
export async function setUserPassword(data: SetUserPasswordRequest) {
  return await httpClient({
    method: 'POST',
    url: `${USER_BASE_ROUTE}/password`,
    data,
  });
}

export type LoginUsingPassword = {
  email: string;
  password: string;
};

export async function loginUsingPassword(data: LoginUsingPassword): Promise<AxiosResponse<LoginUsingPasswordResponse>> {
  return await httpClient({
    method: 'POST',
    url: `${USER_BASE_ROUTE}/authentication`,
    data,
  });
}

export async function signupUsingPassword(
  email: string,
  password: string,
  passwordConfirmation: string
): Promise<AxiosResponse<SignupUsingPasswordResponse>> {
  return await httpClient({
    method: 'POST',
    url: `${USER_BASE_ROUTE}/passwordsignup`,
    data: {
      email,
      password,
      passwordConfirmation,
    },
  });
}

/**
 * To be implemented.
 * @param refreshToken
 */
export async function refreshToken(refreshToken: string): Promise<AxiosResponse<LoginResponseModel>> {
  return await httpClient({
    method: 'POST',
    url: `${USER_BASE_ROUTE}/refresh`,
    data: {
      refreshToken,
    },
  });
}

/**
 * Route to trigger email change flow; email does not get changed until it is verified.
 */
export async function triggerEmailChange(email: string): Promise<AxiosResponse<void>> {
  return await httpClient({
    method: 'POST',
    url: `${USER_BASE_ROUTE}/trigger-email-change`,
    data: {
      email,
    },
  });
}

export async function uploadProfilePicture(file: File): Promise<AxiosResponse<UserProfilePictureUploadResponse>> {
  return await httpClient({
    method: 'POST',
    url: `${USER_BASE_ROUTE}/profile-picture`,
    data: (() => {
      const formData = new FormData();
      formData.append('file', file);
      return formData;
    })(),
    headers: {
      'Content-Type': 'multipart/form-data',
      enctype: 'multipart/form-data',
    },
  });
}

/**
 * Invite users to organization with specified roles.
 * @param users Array of objects containing email and role for each user to invite
 * @param spaceToGiveAccess Optional space ID to give access to
 * @returns {Promise<axios.AxiosResponse<InviteUsersToOrganizationResponse>>}
 */
export async function inviteUsersToOrganization(
  users: Array<{ email: string; role?: OrganizationAccessRoles }>,
  spaceToGiveAccess?: number
): Promise<AxiosResponse<InviteUsersToOrganizationResponse>> {
  const data: {
    users: Array<{ email: string; role?: OrganizationAccessRoles }>;
    organizationId: number;
    spaceToGiveAccess?: number;
  } = {
    users,
    organizationId: (await getCurrentlyActiveOrganization()).id,
  };

  if (spaceToGiveAccess) {
    data['spaceToGiveAccess'] = spaceToGiveAccess;
  }

  return httpClient({
    method: 'POST',
    url: `${USER_BASE_ROUTE}/invite`,
    data,
  });
}

/**
 *
 * @param email
 */
export function checkIfUserExists(email: string): Promise<AxiosResponse<{ signUpMethod: SignUpMethod | null }>> {
  return httpClient({
    method: 'GET',
    url: `${USER_BASE_ROUTE}/exists/${email}`,
  });
}
